import { DownloadTwoTone } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React, { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getOrderInfoSalesOrderItemViewInvoice,
  getSalesIndividualOrderInfo,
} from '../api';
import { useAuth } from '../hooks/useAuth';
import { formatAmountInCurrency } from '../utils';
import {
  IMemberSalesOrderInfoResult,
  IOrderInfoSalesOrderItem,
} from '../utils/types';
import { useUserCheckPermission } from '../hooks/useUserCheckPermission';
import CancelOrderDialog from './CancelOrderDialog';

type OrderInfoSalesOrderItemProps = {
  salesOrderItem: IOrderInfoSalesOrderItem[];
  salesIndividualId: string;
};

export default function OrderInfoSalesOrderItem({
  salesOrderItem,
  salesIndividualId,
}: OrderInfoSalesOrderItemProps) {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const [invoiceLoading, setInvoiceLoading] = useState<boolean>(false);
  const [salesOrderItemInvoicePrint, setSalesOrderItemInvoicePrint] =
    useState('');
  let totalAmount = 0;
  let total = 0;
  let currencyCode = '';
  const { can } = useUserCheckPermission();
  const [cancelOpen, setCancelOpen] = useState(false);
  const [data, setData] = useState<IMemberSalesOrderInfoResult>();

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cancelOpen]);

  async function fetchData() {
    if (!salesIndividualId) return;
    const res = await getSalesIndividualOrderInfo(
      salesIndividualId as string,
      token,
    );
    const salesOrderInfo = await res.json();
    setData(salesOrderInfo);
  }

  salesOrderItem.map((_salesOrderItemItem, i) => {
    totalAmount += _salesOrderItemItem.pricing.amount;
    currencyCode = _salesOrderItemItem.pricing.currency.code;
    total +=
      _salesOrderItemItem.pricing.amount -
      (_salesOrderItemItem.discounts.reduce((total, discount) => {
        const discountAmount =
          typeof discount.amount === 'number'
            ? discount.amount
            : parseFloat(discount.amount) || 0;
        return total + discountAmount;
      }, 0) as number);
  });

  totalAmount = parseFloat(totalAmount.toFixed(2));
  total = parseFloat(total.toFixed(2));

  useEffect(() => {
    if (salesOrderItemInvoicePrint) {
      openInvoiceInNewWindow(salesOrderItemInvoicePrint);
    }
  }, [salesOrderItemInvoicePrint]);

  const openInvoiceInNewWindow = (invoiceHtml: string) => {
    const newWindow = window.open('', '_blank');
    if (newWindow) {
      newWindow.document.write(invoiceHtml);
      newWindow.document.close();
    }
  };

  const fetchOrderInfoSalesOrderItemViewInvoice = async (
    salesOrderItemId: number,
  ) => {
    setInvoiceLoading(true);
    setLoading(true);
    try {
      const res = await getOrderInfoSalesOrderItemViewInvoice(
        salesIndividualId,
        salesOrderItemId,
        token,
      );
      const data = await res.json();
      if (res?.status === 200) {
        setSalesOrderItemInvoicePrint(data);
      } else {
        throw data;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      setError(e.detail || t('loginPage.registerErrorMessage'));
      console.log(error);
    } finally {
      setInvoiceLoading(false);
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box>
        {loading && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            <CircularProgress color="secondary" />
            <Box sx={{ fontSize: '18px', fontWeight: 900, marginTop: '24px' }}>
              {t('balancesQueryPage.loading')}
            </Box>
          </Box>
        )}
      </Box>
      {!loading && (
        <Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="left">
                    {t('orderInfos.materialName')} <br />
                  </TableCell>
                  <TableCell align="center">
                    {t('orderInfos.invoice')}
                  </TableCell>
                  <TableCell align="right">{t('orderInfos.amount')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {salesOrderItem.map((salesOrderItemItem, i) => (
                  <TableRow key={i}>
                    <TableCell align="left">
                      {salesOrderItemItem.quantity > 1
                        ? `${salesOrderItemItem.quantity} x `
                        : ''}
                      {salesOrderItemItem.material.name}
                      <br />
                      <Typography variant="caption" color="text.secondary">
                        {salesOrderItemItem.material.code}
                      </Typography>
                      <Typography variant="caption" color="blue">
                        {can('updateMemberCancelSalesIndividualMaterial') &&
                          salesOrderItemItem.invoiceState ===
                            'NOT_YET_PROCESSED' && (
                            <Typography
                              sx={{ cursor: 'pointer' }}
                              onClick={() => setCancelOpen(true)}
                            >
                              {t('orderDetailPage.cancelOrder')}
                            </Typography>
                          )}
                      </Typography>
                    </TableCell>

                    <TableCell align="center">
                      {salesOrderItemItem.invoiceState ===
                      'COMPLETELY_PROCESSED' ? (
                        <LoadingButton
                          loading={invoiceLoading}
                          color="secondary"
                          size="small"
                          onClick={() =>
                            fetchOrderInfoSalesOrderItemViewInvoice(
                              salesOrderItemItem.id,
                            )
                          }
                          startIcon={<DownloadTwoTone />}
                        >
                          {t('orderInfos.viewInvoice')}
                        </LoadingButton>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {formatAmountInCurrency(
                        salesOrderItemItem.pricing.amount,
                        salesOrderItemItem.pricing.currency.code,
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow
                  sx={{ td: { fontWeight: 900, borderBottom: 'transparent' } }}
                >
                  <TableCell align="left">
                    {t('orderInfos.totalAmount')}
                  </TableCell>
                  <TableCell align="right" colSpan={2}>
                    {formatAmountInCurrency(totalAmount, currencyCode)}
                  </TableCell>
                </TableRow>
                {salesOrderItem.map((salesOrderItemItem, i) => (
                  <Fragment key={i}>
                    {salesOrderItemItem.discounts.map((r: any, i: number) => (
                      <TableRow
                        key={i}
                        sx={{ td: { py: 0.5, borderBottom: 'transparent' } }}
                      >
                        <TableCell align="left">
                          {r.discountType?.name}
                          {r.campaign?.name}
                        </TableCell>
                        <TableCell
                          align="right"
                          colSpan={2}
                          sx={{ color: 'primary.main', fontWeight: 'bold' }}
                        >
                          -{formatAmountInCurrency(r.amount, r.currency.code)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </Fragment>
                ))}
                <TableRow
                  sx={{
                    td: {
                      fontWeight: 900,
                      fontSize: '1.15rem',
                      borderBottom: 'transparent',
                      borderTop: (t) => '1px solid ' + t.palette.grey[300],
                    },
                  }}
                >
                  <TableCell align="left">
                    {t('orderInfos.grandTotal')}
                  </TableCell>
                  <TableCell align="right" colSpan={2}>
                    {formatAmountInCurrency(total, currencyCode)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <CancelOrderDialog
            open={cancelOpen}
            onClose={() => setCancelOpen(false)}
            data={data}
          />
        </Box>
      )}
    </Box>
  );
}
